import React, {
  lazy, Suspense, useEffect, useState
} from 'react';
import {
  string, func, bool, shape, oneOfType
} from 'prop-types';

import { useStore, useConfigService } from '@thd-nucleus/experience-context';
import { QueryProvider, useDataModel } from '@thd-nucleus/data-sources';

import { PromotionProductsLoader } from './PromotionProductsLoader';
import { isCompatiblePromotion } from '../utils/promo-model-utils';
import { getIsFeatureEnabled, isPromotionEnabled } from '../utils/promo-utils';
import { dataModel } from './dataModelAnchorItem';
import { useMACheckAvailability } from '../hooks/useMACheckAvailability';
import { EXPERIENCE_TAGS } from '../utils/constants';

const PromotionProductsLazy = lazy(() => import(
  /* webpackChunkName: "promotion-products-lazy" */
  './PromotionProductsLazy'
).then((mod) => {
  return {
    default: (props) => {
      const { PromotionProductsLazy: PromotionProductsComp } = mod;

      return (
        <QueryProvider cacheKey="discounted-items" defaultVariables={{ skipSubscribeAndSave: true }}>
          {/* eslint-disable-next-line react/jsx-props-no-spreading */}
          <PromotionProductsComp {...props} />
        </QueryProvider>
      );
    }
  };
}));

export const PromotionProducts = ({
  itemId,
  bogoEnabled: bogoEnabledProp,
  msbEnabled: msbEnabledProp,
  bmsmEnabled: bmsmEnabledProp,
  backwardsBogoEnabled: backwardsBogoEnabledProp,
  isMultiSelect,
}) => {
  const [mounted, setMounted] = useState(false);
  const bogoEnabled = getIsFeatureEnabled({
    defaultValue: true,
    prop: bogoEnabledProp,
    config: useConfigService('fs-prop:buy-area__promotion-products--bogoEnabled'),
  });
  const msbEnabled = getIsFeatureEnabled({
    defaultValue: true,
    prop: msbEnabledProp,
    config: useConfigService('fs-prop:buy-area__promotion-products--msbEnabled'),
  });
  const bmsmEnabled = getIsFeatureEnabled({
    defaultValue: true,
    prop: bmsmEnabledProp,
    config: useConfigService('fs-prop:buy-area__promotion-products--bmsmEnabled'),
  });
  const backwardsBogoEnabled = getIsFeatureEnabled({
    defaultValue: false,
    prop: backwardsBogoEnabledProp,
    config: useConfigService('fs-prop:buy-area__promotion-products--backwardsBogoEnabled'),
  });

  const isEnabled = bogoEnabled || msbEnabled || bmsmEnabled;

  const store = useStore();

  // this is the request for the anchor item
  const { data } = useDataModel('product', {
    variables: {
      itemId,
      storeId: store.storeId,
      zipCode: store.storeZip,
    },
    skip: !isEnabled,
  });

  useEffect(() => {
    setMounted(true);
  }, []);

  const anchorItem = data?.product;
  const experienceTag = anchorItem?.pricing?.promotion?.experienceTag;

  const checkAvailabilityFulfillment = useMACheckAvailability({
    anchorItem,
    skip: experienceTag !== EXPERIENCE_TAGS.BOGO
  });

  if (!isCompatiblePromotion(anchorItem) || !isPromotionEnabled({
    bogoEnabled, backwardsBogoEnabled, msbEnabled, bmsmEnabled, anchorItem
  })) {
    return null;
  }

  if (!mounted) {
    return <PromotionProductsLoader experienceTag={experienceTag} />;
  }

  return (
    <Suspense fallback={<PromotionProductsLoader experienceTag={experienceTag} />}>
      {checkAvailabilityFulfillment.isLoading
        ? (
          <PromotionProductsLoader experienceTag={experienceTag} />
        )
        : (
          <PromotionProductsLazy
            anchorItem={anchorItem}
            store={store}
            isMultiSelect={isMultiSelect && isMultiSelect(anchorItem)}
            checkAvailabilityFulfillment={checkAvailabilityFulfillment}
          />
        )}
    </Suspense>
  );
};

PromotionProducts.displayName = 'PromotionProducts';

PromotionProducts.propTypes = {
  /**
   * The itemId of the "Anchor Item". It is the promotion associated with that item that is displayed
   */
  itemId: string.isRequired,
  /**
   * Enables BOGO promotions. The config service may be used to enable or disable as well. defaults enabled
   */
  bogoEnabled: oneOfType([bool, shape({})]),
  /**
   * Enables MSB promotions.  The config service may be used to enable or disable as well. defaults enabled
   */
  msbEnabled: oneOfType([bool, shape({})]),
  /**
   * Enables BMSM promotions. The config service may be used to enable or disable as well. defaults enabled
   */
  bmsmEnabled: oneOfType([bool, shape({})]),
  /**
   * Enables Backwards BOGO promotions. The config service may be used to enable or disable as well. defaults disabled
   */
  backwardsBogoEnabled: oneOfType([bool, shape({})]),
  /**
   * For BMSM promotions for major appliance products
   */
  isMultiSelect: func,
};

PromotionProducts.defaultProps = {
  bogoEnabled: null,
  msbEnabled: null,
  bmsmEnabled: null,
  backwardsBogoEnabled: null,
  isMultiSelect: () => false,
};

PromotionProducts.dataModel = dataModel;
